import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { CatalogContentBundle, CatalogEditDraft } from '@/types/catalog.ts';
import { useDraftActions } from '@/hooks/useCatalogEditDraft.ts';
import CatalogDiffBlock from './CatalogDiffBlock.tsx';

interface Props {
  draft: CatalogEditDraft;
  onUpdated: (draft: CatalogEditDraft) => void;
}

// Case-insensitive bundle normalizer. Backend used to serialize with PascalCase
// (Slug/Label/Briefing/ParentSlug) before the camelCase fix; some drafts persisted
// in DB still carry that shape. Lookup all possible casings so old + new drafts
// both render correctly.
function normalizeBundle(raw: unknown): CatalogContentBundle | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const get = (...keys: string[]): unknown => {
    for (const k of keys) if (r[k] !== undefined) return r[k];
    return undefined;
  };
  const slug = get('slug', 'Slug');
  const label = get('label', 'Label');
  const briefing = get('briefing', 'Briefing');
  const parentSlug = get('parentSlug', 'ParentSlug');
  return {
    slug: typeof slug === 'string' ? slug : '',
    label: typeof label === 'string' ? label : '',
    briefing: typeof briefing === 'string' ? briefing : null,
    parentSlug: typeof parentSlug === 'string' ? parentSlug : null,
  };
}

function parseBundle(json: string | null | undefined): CatalogContentBundle | null {
  if (!json) return null;
  try {
    return normalizeBundle(JSON.parse(json));
  } catch {
    return null;
  }
}

function parseTranslated(json: string | null | undefined): Record<string, CatalogContentBundle> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>;
    const normalized: Record<string, CatalogContentBundle> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const bundle = normalizeBundle(v);
      if (bundle) normalized[k.toLowerCase()] = bundle;
    }
    return normalized;
  } catch {
    return {};
  }
}

export default function CatalogDiffViewer({ draft, onUpdated }: Props) {
  const { t } = useTranslation();
  const { updateTranslation } = useDraftActions();

  const source = useMemo(() => parseBundle(draft.sourceContentJson), [draft.sourceContentJson]);
  const translated = useMemo(() => parseTranslated(draft.translatedContentJson), [draft.translatedContentJson]);

  const sourceLang = draft.sourceLanguage.toLowerCase();
  const targetLangs = useMemo(() => Object.keys(translated).filter((l) => l !== sourceLang), [translated, sourceLang]);

  const labelText = t('superuser.catalog.form.label', { defaultValue: 'Etiqueta' });
  const briefingText = t('superuser.catalog.form.briefing', { defaultValue: 'Briefing' });

  async function saveField(language: string, field: 'label' | 'briefing', value: string) {
    const current = translated[language] ?? { slug: source?.slug ?? '', label: '', briefing: '' };
    const nextBundle: CatalogContentBundle = {
      slug: source?.slug ?? current.slug,
      parentSlug: source?.parentSlug ?? current.parentSlug ?? null,
      label: field === 'label' ? value : current.label,
      briefing: field === 'briefing' ? value : (current.briefing ?? null),
    };
    try {
      const updated = await updateTranslation(draft.id, { language, content: nextBundle });
      onUpdated(updated);
      toast.success(t('superuser.catalog.toast.translationSaved', {
        defaultValue: 'Traducción actualizada.',
      }));
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'ERROR';
      toast.error(t(`superuser.catalog.errors.${raw}`, { defaultValue: raw }));
    }
  }

  if (!source) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        SOURCE_CONTENT_INVALID
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="space-y-3">
        <header className="flex items-center gap-2 text-sm font-medium">
          <Lock className="size-4 text-foreground" />
          <span className="font-mono uppercase">{sourceLang}</span>
          <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-xs">
            {t('superuser.catalog.diff.humanEdited', { defaultValue: 'Editado por humano' })}
          </span>
        </header>
        <CatalogDiffBlock
          field="label"
          fieldLabel={labelText}
          language={sourceLang}
          text={source.label}
          editable={false}
          onSave={async () => {}}
        />
        <CatalogDiffBlock
          field="briefing"
          fieldLabel={briefingText}
          language={sourceLang}
          text={source.briefing ?? ''}
          editable={false}
          onSave={async () => {}}
        />
      </section>

      <section className="space-y-4">
        {targetLangs.length === 0 && (
          <div className="rounded-md border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
            {t('superuser.catalog.diff.noTranslations', { defaultValue: 'Sin traducciones todavía.' })}
          </div>
        )}
        {targetLangs.map((lang) => {
          const bundle = translated[lang];
          return (
            <div key={lang} className="space-y-3">
              <header className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="size-4 text-primary" />
                <span className="font-mono uppercase">{lang}</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                  {t('superuser.catalog.diff.iaTranslated', { defaultValue: 'Generado por IA' })}
                </span>
              </header>
              <CatalogDiffBlock
                field="label"
                fieldLabel={labelText}
                language={lang}
                text={bundle.label}
                editable
                onSave={(v) => saveField(lang, 'label', v)}
              />
              <CatalogDiffBlock
                field="briefing"
                fieldLabel={briefingText}
                language={lang}
                text={bundle.briefing ?? ''}
                editable
                onSave={(v) => saveField(lang, 'briefing', v)}
              />
            </div>
          );
        })}
      </section>
    </div>
  );
}
